import styles from "./client-settings-alert-status.module.css";

import {
  AlertSettings,
  AlertType,
  ClientProfileAlertsData,
  ClientProfileSettings,
  DelayType,
} from "../store/types";
import { inject, observer } from "mobx-react";
import classnames from "classnames";
import Delay from "./alert-delay";
import Sound from "./alert-sound";
import { useTranslation } from "react-i18next";
import { ReactComponent as Check } from "../svg/check.svg";
import { ReactComponent as Warning } from "../svg/warning.svg";
import { ReactComponent as Dash } from "../svg/dash.svg";
import { ChangeEvent } from "react";
import { FeatureStore } from "../../../store";
import { SwitchInput } from "../../../components/form-inputs";
import OverflowLabel from "../../../components/OverflowLabel";
import { ClientProfileStore } from "../store/store";

interface ClientAlertGroupProps {
  featureStore?: FeatureStore;
  clientProfileStore?: ClientProfileStore;
  clientSettings: ClientProfileSettings;
  clientProfileAlerts: ClientProfileAlertsData;
}

export interface IAlertInfo {
  settings: AlertSettings;
  alertName: string;
  enabled: boolean;
}

function ClientSettingsAlertGroup(props: ClientAlertGroupProps) {
  const { t } = useTranslation("client-profile");
  const { t: alertGridContext } = useTranslation("alert-grid");
  const { clientProfileAlerts } = props;

  const createSortedAlerts = () => {
    const { clientProfileAlerts, featureStore, clientProfileStore } = props;
    const { featureList } = featureStore!;
    const profileAlerts = clientProfileAlerts.alerts;

    const hasTelecareCapability =
      clientProfileStore!.clientProfileDetails.hasTelecareCapability;

    let supportedVoiceAlertText = "";
    if (!hasTelecareCapability) {
      supportedVoiceAlertText = ` (${alertGridContext("NotSupported")})`;
    }

    const canUpdateVoiceAlert =
      clientProfileStore!.dataValidationStatus.acousticSettingsValid;

    const internalAlerts = [
      {
        alertId: 7,
        name: t("Voice alert") + supportedVoiceAlertText,
        enabled: hasTelecareCapability && canUpdateVoiceAlert,
      },
      { alertId: 4, name: t("Inactivity"), enabled: true },
      { alertId: 1, name: t("Out of bed"), enabled: true },
      { alertId: 2, name: t("Bathroom"), enabled: true },
      { alertId: 8, name: t("Long time in bed"), enabled: true },
      { alertId: 3, name: t("Out of room"), enabled: true },
      { alertId: 5, name: t("Visitor"), enabled: true },
    ];

    if (featureList.isVoiceAlertAvailable === false) {
      const voiceAlertIndex = internalAlerts.findIndex((alert) => {
        return alert.alertId === 7;
      });

      if (voiceAlertIndex >= 0) {
        internalAlerts.splice(voiceAlertIndex, 1);
      }
    }

    const alerts: IAlertInfo[] = [];

    internalAlerts.forEach((alert) => {
      const data = profileAlerts.find((pa) => pa.alertId === alert.alertId);
      if (!!data)
        alerts.push({
          settings: data,
          alertName: alert.name,
          enabled: alert.enabled,
        });
    });

    profileAlerts
      .filter((d) => d.type === AlertType.External)
      .sort((a: AlertSettings, b: AlertSettings) => {
        if (a.name < b.name) {
          return -1;
        }

        if (a.name === b.name) {
          return 0;
        }

        return 1;
      })
      .forEach((alert) => {
        alerts.push({ settings: alert, alertName: alert.name, enabled: true });
      });

    return alerts;
  };

  const render = () => {
    if (!clientProfileAlerts || !clientProfileAlerts.alerts) return null;

    const alerts = createSortedAlerts();
    const renderAlerts = alerts.map((alert) => {
      return (
        <div
          className={`${styles["item"]} ${styles["even-tab"]} ${styles["settings-even-tab"]}`}
          key={alert.settings.alertId}
        >
          <div
            className={classnames(
              `${styles["settings-text"]} ${styles["settings-col-1"]}`,
              {
                disabled: !alert.settings.isActive,
              }
            )}
          >
            <OverflowLabel text={alert.alertName} />
          </div>
          {renderAlertStatus(alert)}
          {renderAlertSettings(alert)}
          {renderAlertSensorsStatus(alert)}
        </div>
      );
    });
    return <>{renderAlerts}</>;
  };

  const renderAlertStatus = (alert: IAlertInfo) => {
    return renderToggleSwitch(alert);
  };

  const renderToggleSwitch = (alert: IAlertInfo) => {
    return (
      <div className={styles["settings-col-2"]}>
        <SwitchInput
          checked={alert.settings.isActive}
          disabled={!alert.enabled}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            alert.settings.isActive = !alert.settings.isActive;
            props.clientSettings.hasChanges = true;
          }}
        />
      </div>
    );
  };

  const renderAlertSettings = (alert: IAlertInfo) => {
    return (
      <div
        className={classnames(
          `${styles["settings-text"]} ${styles["settings-col-3"]}`,
          {
            disabled: !alert.settings.isActive,
          }
        )}
      >
        {alert.settings.delayType !== DelayType.None && (
          <Delay
            alert={alert}
            onChange={() => {
              props.clientSettings.hasChanges = true;
            }}
          />
        )}
        <Sound
          alert={alert}
          onChange={() => {
            props.clientSettings.hasChanges = true;
          }}
        />
      </div>
    );
  };

  const renderAlertSensorsStatus = (alert: IAlertInfo) => {
    return (
      <div
        className={classnames(
          `${styles["settings-text"]} ${styles["settings-col-4"]}`,
          {
            disabled: !alert.settings.isActive,
          }
        )}
      >
        {getAlertSensorsStatus(alert)}
      </div>
    );
  };

  const getAlertSensorsStatus = (alert: IAlertInfo) => {
    if (
      alert.enabled &&
      alert.settings.hasSensors &&
      alert.settings.areSensorsActive
    )
      return <Check width="40px" height="40px" />;

    if (
      alert.enabled &&
      alert.settings.hasSensors &&
      !alert.settings.areSensorsActive
    )
      return <Warning className="critical" width="40px" height="40px" />;

    if (alert.enabled || !alert.settings.hasSensors)
      return <Dash width="40px" color="#173544" />;
  };

  return render();
}

export default inject(
  "featureStore",
  "clientProfileStore"
)(observer(ClientSettingsAlertGroup));
